import { useQuery } from '@tanstack/react-query'
import { ShoppingBag } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'

import { Button, Notice } from '@aeris/ui'

import { fetchStoreOrderPendingCount } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { playChime } from '../lib/chime.ts'
import { useWorkspace } from '../lib/workspace.ts'

/**
 * O pedido da loja chegou — F224.
 *
 * O cliente fecha o carrinho no site e manda o pedido no WhatsApp da
 * vendedora; aqui, em qualquer tela, ela vê "1 pedido da loja esperando você",
 * com o mesmo bipe do Pix (F205), e abre a fila para aprovar.
 *
 * A contagem e não um fluxo de eventos: pergunta a cada 30 segundos. O pedido
 * da loja espera a vendedora por minutos, não por segundos, e a contagem não
 * depende da permissão do Pix que o fluxo de avisos exige.
 *
 * A vendedora vê os dela. Quem gerencia a loja (`storefront.manage`) e não tem
 * pedido seu vê os da loja inteira.
 *
 * F248: montado DENTRO de cada moldura (o shell do navegador e a área de
 * trabalho do Modo PC), e não acima delas. No Modo PC, "Abrir" abre a janela
 * de Pedidos da loja — de fora da área de trabalho, o `navigate` trocava a URL
 * que ninguém lia e o aviso sumia sem abrir nada. E ele vai para o canto dos
 * avisos (`Notice`), em vez de ficar por cima do aviso do leitor de código.
 */

const POLL_MS = 30_000

export const StoreOrderAlert = (): ReactNode => {
  const { allows, hasFeature } = useAuth()
  if (!hasFeature('loja-online') || !allows('sales.read')) return null
  return <StoreOrderCount manages={allows('storefront.manage')} />
}

const StoreOrderCount = ({
  manages,
}: {
  readonly manages: boolean
}): ReactNode => {
  const workspace = useWorkspace()
  const counts = useQuery({
    queryKey: ['store-orders', 'pending-count'],
    queryFn: async ({ signal }) => await fetchStoreOrderPendingCount(signal),
    refetchInterval: POLL_MS,
  })
  const shown =
    counts.data === undefined
      ? 0
      : counts.data.mine > 0 || !manages
        ? counts.data.mine
        : counts.data.pending
  const mine = (counts.data?.mine ?? 0) > 0

  // O bipe só quando o número SOBE; o "Depois" esconde até chegar outro.
  const last = useRef<number | null>(null)
  const [hiddenAt, setHiddenAt] = useState<number | null>(null)
  useEffect(() => {
    if (counts.data === undefined) return
    if (last.current !== null && shown > last.current) playChime()
    last.current = shown
  }, [counts.data, shown])

  if (shown === 0 || hiddenAt === shown) return null

  return (
    <Notice>
      <section
        aria-label="Pedidos da loja"
        className="flex flex-col gap-2 rounded-(--radius-lg) border border-(--border-strong) bg-(--surface-raised) p-3 shadow-(--shadow-md)"
      >
        <p
          role="status"
          className="flex items-center gap-2 font-(--weight-semibold)"
        >
          <ShoppingBag aria-hidden="true" className="h-4 w-4" />
          {shown === 1
            ? `1 pedido da loja esperando${mine ? ' você' : ''}`
            : `${String(shown)} pedidos da loja esperando${mine ? ' você' : ''}`}
        </p>
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setHiddenAt(shown)
            }}
          >
            Depois
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setHiddenAt(shown)
              workspace.open({ module: 'pedidos-loja' })
            }}
          >
            Abrir
          </Button>
        </div>
      </section>
    </Notice>
  )
}
